import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { BellIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, InfoIcon, FilterIcon, ChevronDownIcon, SearchIcon, MailIcon, TrashIcon, CheckIcon, EyeIcon, MoreHorizontalIcon, RefreshCwIcon, SettingsIcon, PlusIcon, BellOffIcon, ClockIcon, CalendarIcon, BuildingIcon, UserIcon, FileTextIcon, CreditCardIcon, TagIcon, MessageCircleIcon, AlertTriangleIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
// Mock data for notifications
const generateMockNotifications = (count: number, mdaId?: string) => {
  const types = ['info', 'success', 'warning', 'error', 'system'];
  const categories = ['application', 'payment', 'user', 'system', 'service'];
  const mdas = ['Ministry of Finance', 'Ministry of Lands and Surveys', 'Ministry of Commerce and Industry', 'Federal Road Safety Commission', 'Ministry of Education'];
  const mockData = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const category = categories[Math.floor(Math.random() * categories.length)];
    const mda = mdas[Math.floor(Math.random() * mdas.length)];
    const isRead = Math.random() > 0.4;
    const daysAgo = Math.floor(Math.random() * 14);
    const date = new Date(now);
    date.setDate(date.getDate() - daysAgo);
    let title = '';
    let message = '';
    let icon = InfoIcon;
    // Generate title and message based on type and category
    if (category === 'application') {
      if (type === 'success') {
        title = 'Application Approved';
        message = `Application #${10000 + Math.floor(Math.random() * 90000)} has been approved.`;
        icon = CheckCircleIcon;
      } else if (type === 'warning') {
        title = 'Application Requires Review';
        message = `Application #${10000 + Math.floor(Math.random() * 90000)} requires additional review.`;
        icon = AlertCircleIcon;
      } else if (type === 'error') {
        title = 'Application Rejected';
        message = `Application #${10000 + Math.floor(Math.random() * 90000)} has been rejected.`;
        icon = XCircleIcon;
      } else {
        title = 'New Application Submitted';
        message = `A new application #${10000 + Math.floor(Math.random() * 90000)} has been submitted.`;
        icon = FileTextIcon;
      }
    } else if (category === 'payment') {
      if (type === 'success') {
        title = 'Payment Successful';
        message = `Payment of ₦${1000 + Math.floor(Math.random() * 100000)} has been received.`;
        icon = CheckCircleIcon;
      } else if (type === 'warning') {
        title = 'Payment Pending';
        message = `Payment of ₦${1000 + Math.floor(Math.random() * 100000)} is pending verification.`;
        icon = ClockIcon;
      } else if (type === 'error') {
        title = 'Payment Failed';
        message = `Payment of ₦${1000 + Math.floor(Math.random() * 100000)} has failed.`;
        icon = XCircleIcon;
      } else {
        title = 'New Payment Initiated';
        message = `A new payment of ₦${1000 + Math.floor(Math.random() * 100000)} has been initiated.`;
        icon = CreditCardIcon;
      }
    } else if (category === 'user') {
      if (type === 'info') {
        title = 'New User Registration';
        message = 'A new user has registered on the platform.';
        icon = UserIcon;
      } else if (type === 'warning') {
        title = 'User Account Warning';
        message = 'A user account has triggered a security warning.';
        icon = AlertCircleIcon;
      } else if (type === 'system') {
        title = 'User Profile Updated';
        message = 'A user has updated their profile information.';
        icon = UserIcon;
      } else {
        title = 'User Support Request';
        message = 'A user has submitted a new support request.';
        icon = MessageCircleIcon;
      }
    } else if (category === 'service') {
      if (type === 'info') {
        title = 'Service Update';
        message = `The ${['Tax Filing', 'Business Registration', 'Vehicle Registration', 'Land Documentation'][Math.floor(Math.random() * 4)]} service has been updated.`;
        icon = InfoIcon;
      } else if (type === 'success') {
        title = 'New Service Added';
        message = `A new service has been added to the ${mda}.`;
        icon = PlusIcon;
      } else if (type === 'warning') {
        title = 'Service Maintenance';
        message = `The ${['Tax Filing', 'Business Registration', 'Vehicle Registration', 'Land Documentation'][Math.floor(Math.random() * 4)]} service will undergo maintenance soon.`;
        icon = SettingsIcon;
      } else {
        title = 'Service Unavailable';
        message = `The ${['Tax Filing', 'Business Registration', 'Vehicle Registration', 'Land Documentation'][Math.floor(Math.random() * 4)]} service is temporarily unavailable.`;
        icon = AlertTriangleIcon;
      }
    } else {
      // System notifications
      if (type === 'info') {
        title = 'System Update';
        message = 'The system will undergo maintenance tonight at 2 AM.';
        icon = InfoIcon;
      } else if (type === 'warning') {
        title = 'System Performance';
        message = 'The system is experiencing higher than normal load.';
        icon = AlertCircleIcon;
      } else if (type === 'error') {
        title = 'System Error';
        message = 'A system error has been detected and is being investigated.';
        icon = AlertTriangleIcon;
      } else {
        title = 'System Notification';
        message = 'A new system update has been deployed.';
        icon = BellIcon;
      }
    }
    // Only include notifications for the specified MDA if mdaId is provided
    if (!mdaId || mda === mdaId) {
      mockData.push({
        id: `notif-${i}-${Date.now()}`,
        title,
        message,
        type,
        category,
        mda,
        isRead,
        date: date.toISOString(),
        icon
      });
    }
  }
  return mockData;
};
// Format relative time
const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric'
  };
  return date.toLocaleDateString(undefined, options);
};
const Notifications: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<any[]>([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showNotificationDetail, setShowNotificationDetail] = useState<string | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [notificationsToDelete, setNotificationsToDelete] = useState<string[]>([]);
  const {
    hasPermission,
    isSuperAdmin,
    getMdaId,
    getMdaName
  } = useAdminAuth();
  const canManageNotifications = hasPermission('notifications:manage');
  // Load notifications
  useEffect(() => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      let allNotifications = [];
      // If super admin, load all notifications
      // If MDA admin, only load notifications for their MDA
      if (isSuperAdmin()) {
        allNotifications = generateMockNotifications(50);
      } else {
        const mdaName = getMdaName();
        allNotifications = generateMockNotifications(20, mdaName);
      }
      setNotifications(allNotifications);
      setFilteredNotifications(allNotifications);
      setUnreadCount(allNotifications.filter((n: any) => !n.isRead).length);
      setIsLoading(false);
    }, 1000);
  }, [isSuperAdmin, getMdaId, getMdaName]);
  // Apply filters
  useEffect(() => {
    let filtered = [...notifications];
    // Filter by read/unread status
    if (activeFilter === 'unread') {
      filtered = filtered.filter(n => !n.isRead);
    } else if (activeFilter === 'read') {
      filtered = filtered.filter(n => n.isRead);
    }
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(n => n.category === categoryFilter);
    }
    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(n => n.type === typeFilter);
    }
    // Apply time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      const dayInMs = 24 * 60 * 60 * 1000;
      if (timeFilter === 'today') {
        filtered = filtered.filter(n => {
          const date = new Date(n.date);
          return now.getTime() - date.getTime() < dayInMs;
        });
      } else if (timeFilter === 'week') {
        filtered = filtered.filter(n => {
          const date = new Date(n.date);
          return now.getTime() - date.getTime() < 7 * dayInMs;
        });
      } else if (timeFilter === 'month') {
        filtered = filtered.filter(n => {
          const date = new Date(n.date);
          return now.getTime() - date.getTime() < 30 * dayInMs;
        });
      }
    }
    // Apply search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(n => n.title.toLowerCase().includes(query) || n.message.toLowerCase().includes(query) || n.mda.toLowerCase().includes(query));
    }
    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    setFilteredNotifications(filtered);
  }, [notifications, activeFilter, searchQuery, categoryFilter, typeFilter, timeFilter, sortOrder]);
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Toggle dropdown
  const toggleDropdown = (dropdown: string) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };
  // Handle select all notifications
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n.id));
    }
    setSelectAll(!selectAll);
  };
  // Handle select notification
  const handleSelectNotification = (id: string) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(selectedNotifications.filter(n => n !== id));
      setSelectAll(false);
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
      if (selectedNotifications.length + 1 === filteredNotifications.length) {
        setSelectAll(true);
      }
    }
  };
  // Mark as read
  const markAsRead = (ids: string[]) => {
    setIsMarkingRead(true);
    // Simulate API call delay
    setTimeout(() => {
      const updatedNotifications = notifications.map(n => {
        if (ids.includes(n.id)) {
          return {
            ...n,
            isRead: true
          };
        }
        return n;
      });
      setNotifications(updatedNotifications);
      setUnreadCount(updatedNotifications.filter(n => !n.isRead).length);
      setSelectedNotifications([]);
      setSelectAll(false);
      setIsMarkingRead(false);
    }, 500);
  };
  // Delete notifications
  const deleteNotifications = (ids: string[]) => {
    setIsDeleting(true);
    // Simulate API call delay
    setTimeout(() => {
      const updatedNotifications = notifications.filter(n => !ids.includes(n.id));
      setNotifications(updatedNotifications);
      setUnreadCount(updatedNotifications.filter(n => !n.isRead).length);
      setSelectedNotifications([]);
      setSelectAll(false);
      setIsDeleting(false);
      setShowConfirmDelete(false);
      setNotificationsToDelete([]);
    }, 500);
  };
  // Show notification detail
  const viewNotificationDetail = (id: string) => {
    // Mark as read if not already
    if (!notifications.find(n => n.id === id)?.isRead) {
      markAsRead([id]);
    }
    setShowNotificationDetail(id);
  };
  // Refresh notifications
  const refreshNotifications = () => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      let allNotifications = [];
      // If super admin, load all notifications
      // If MDA admin, only load notifications for their MDA
      if (isSuperAdmin()) {
        allNotifications = generateMockNotifications(50);
      } else {
        const mdaName = getMdaName();
        allNotifications = generateMockNotifications(20, mdaName);
      }
      setNotifications(allNotifications);
      setFilteredNotifications(allNotifications);
      setUnreadCount(allNotifications.filter((n: any) => !n.isRead).length);
      setIsLoading(false);
      setSelectedNotifications([]);
      setSelectAll(false);
    }, 1000);
  };
  // Confirm delete
  const confirmDelete = (ids: string[]) => {
    setNotificationsToDelete(ids);
    setShowConfirmDelete(true);
  };
  // Get notification icon based on type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="text-green-500" size={20} data-id="element-2435" />;
      case 'warning':
        return <AlertCircleIcon className="text-yellow-500" size={20} data-id="element-2436" />;
      case 'error':
        return <XCircleIcon className="text-red-500" size={20} data-id="element-2437" />;
      case 'system':
        return <SettingsIcon className="text-purple-500" size={20} data-id="element-2438" />;
      default:
        return <InfoIcon className="text-blue-500" size={20} data-id="element-2439" />;
    }
  };
  // Get notification badge color based on type
  const getNotificationBadgeClass = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'system':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };
  // Get notification category badge
  const getNotificationCategoryBadge = (category: string) => {
    switch (category) {
      case 'application':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2440">
            <FileTextIcon size={12} className="mr-1" data-id="element-2441" />
            Application
          </span>;
      case 'payment':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-2442">
            <CreditCardIcon size={12} className="mr-1" data-id="element-2443" />
            Payment
          </span>;
      case 'user':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-2444">
            <UserIcon size={12} className="mr-1" data-id="element-2445" />
            User
          </span>;
      case 'service':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2446">
            <TagIcon size={12} className="mr-1" data-id="element-2447" />
            Service
          </span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2448">
            <InfoIcon size={12} className="mr-1" data-id="element-2449" />
            System
          </span>;
    }
  };
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-2450">
        <div className="flex flex-col items-center" data-id="element-2451">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4" data-id="element-2452"></div>
          <p className="text-gray-600" data-id="element-2453">Loading notifications...</p>
        </div>
      </div>;
  }
  return <div className="flex h-screen bg-gray-100" data-id="element-2454">
      {/* Sidebar - hidden on mobile unless toggled */}
      <div className={`${isSidebarOpen ? 'block' : 'hidden'} lg:block fixed inset-y-0 left-0 z-50 lg:relative lg:z-0`} data-id="element-2455">
        <AdminSidebar data-id="element-2456" />
      </div>
      {/* Backdrop for mobile sidebar */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-2457"></div>}
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2458">
        <AdminHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} data-id="element-2459" />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-2460">
          {/* Page Header */}
          <div className="mb-6" data-id="element-2461">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-2462">
              Notifications
              {!isSuperAdmin() && <span className="ml-2 text-lg font-normal text-gray-600" data-id="element-2463">
                  ({getMdaName()})
                </span>}
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-2464">
              {isSuperAdmin() ? 'Manage and review all system notifications' : `Manage and review notifications for ${getMdaName()}`}
            </p>
          </div>
          {/* Notification Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-2465">
            <div className="bg-white rounded-lg shadow p-4" data-id="element-2466">
              <div className="flex justify-between items-start" data-id="element-2467">
                <div data-id="element-2468">
                  <p className="text-sm font-medium text-gray-500" data-id="element-2469">
                    Total Notifications
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2470">
                    {notifications.length}
                  </p>
                </div>
                <div className="p-2 bg-blue-100 rounded-md" data-id="element-2471">
                  <BellIcon size={20} className="text-blue-600" data-id="element-2472" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4" data-id="element-2473">
              <div className="flex justify-between items-start" data-id="element-2474">
                <div data-id="element-2475">
                  <p className="text-sm font-medium text-gray-500" data-id="element-2476">
                    Unread Notifications
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2477">
                    {unreadCount}
                  </p>
                </div>
                <div className="p-2 bg-red-100 rounded-md" data-id="element-2478">
                  <MailIcon size={20} className="text-red-600" data-id="element-2479" />
                </div>
              </div>
              {unreadCount > 0 && <button onClick={() => markAsRead(notifications.filter(n => !n.isRead).map(n => n.id))} className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center" data-id="element-2480">
                  <CheckIcon size={14} className="mr-1" data-id="element-2481" />
                  Mark all as read
                </button>}
            </div>
            <div className="bg-white rounded-lg shadow p-4" data-id="element-2482">
              <div className="flex justify-between items-start" data-id="element-2483">
                <div data-id="element-2484">
                  <p className="text-sm font-medium text-gray-500" data-id="element-2485">
                    Recent Activity
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2486">
                    {notifications.filter(n => {
                    const date = new Date(n.date);
                    const now = new Date();
                    return now.getTime() - date.getTime() < 24 * 60 * 60 * 1000;
                  }).length}
                  </p>
                </div>
                <div className="p-2 bg-green-100 rounded-md" data-id="element-2487">
                  <ClockIcon size={20} className="text-green-600" data-id="element-2488" />
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-600" data-id="element-2489">
                Notifications from the last 24 hours
              </p>
            </div>
          </div>
          {/* Notification Controls */}
          <div className="bg-white rounded-lg shadow mb-6" data-id="element-2490">
            <div className="p-4 border-b border-gray-200" data-id="element-2491">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2492">
                {/* Search */}
                <div className="relative flex-1" data-id="element-2493">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2494">
                    <SearchIcon size={18} className="text-gray-400" data-id="element-2495" />
                  </div>
                  <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder="Search notifications" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2496" />
                </div>
                {/* Filters */}
                <div className="flex flex-wrap gap-2" data-id="element-2497">
                  <div className="relative" data-id="element-2498">
                    <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={() => toggleDropdown('filter')} data-id="element-2499">
                      <FilterIcon size={18} className="mr-2" data-id="element-2500" />
                      Filters
                      <ChevronDownIcon size={18} className="ml-2" data-id="element-2501" />
                    </button>
                    {activeDropdown === 'filter' && <div className="origin-top-right absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-2502">
                        <div className="py-1 p-3" data-id="element-2503">
                          <div className="mb-3" data-id="element-2504">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2505">
                              Category
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-2506">
                              <option value="all" data-id="element-2507">All Categories</option>
                              <option value="application" data-id="element-2508">Applications</option>
                              <option value="payment" data-id="element-2509">Payments</option>
                              <option value="user" data-id="element-2510">Users</option>
                              <option value="service" data-id="element-2511">Services</option>
                              <option value="system" data-id="element-2512">System</option>
                            </select>
                          </div>
                          <div className="mb-3" data-id="element-2513">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2514">
                              Type
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-id="element-2515">
                              <option value="all" data-id="element-2516">All Types</option>
                              <option value="info" data-id="element-2517">Information</option>
                              <option value="success" data-id="element-2518">Success</option>
                              <option value="warning" data-id="element-2519">Warning</option>
                              <option value="error" data-id="element-2520">Error</option>
                              <option value="system" data-id="element-2521">System</option>
                            </select>
                          </div>
                          <div className="mb-3" data-id="element-2522">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2523">
                              Time Period
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={timeFilter} onChange={e => setTimeFilter(e.target.value)} data-id="element-2524">
                              <option value="all" data-id="element-2525">All Time</option>
                              <option value="today" data-id="element-2526">Today</option>
                              <option value="week" data-id="element-2527">This Week</option>
                              <option value="month" data-id="element-2528">This Month</option>
                            </select>
                          </div>
                          <div className="mb-3" data-id="element-2529">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2530">
                              Sort Order
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={sortOrder} onChange={e => setSortOrder(e.target.value)} data-id="element-2531">
                              <option value="newest" data-id="element-2532">Newest First</option>
                              <option value="oldest" data-id="element-2533">Oldest First</option>
                            </select>
                          </div>
                          <div className="flex justify-between pt-2" data-id="element-2534">
                            <button className="text-sm text-gray-700 hover:text-gray-900" onClick={() => {
                          setCategoryFilter('all');
                          setTypeFilter('all');
                          setTimeFilter('all');
                          setSortOrder('newest');
                        }} data-id="element-2535">
                              Reset Filters
                            </button>
                            <button className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700" onClick={() => setActiveDropdown(null)} data-id="element-2536">
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'all' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('all')} data-id="element-2537">
                    All
                  </button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'unread' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('unread')} data-id="element-2538">
                    Unread
                  </button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'read' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('read')} data-id="element-2539">
                    Read
                  </button>
                  <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={refreshNotifications} disabled={isLoading} data-id="element-2540">
                    <RefreshCwIcon size={18} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} data-id="element-2541" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
            {/* Selected notifications actions */}
            {selectedNotifications.length > 0 && <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between" data-id="element-2542">
                <div className="flex items-center" data-id="element-2543">
                  <span className="text-sm text-gray-700 mr-4" data-id="element-2544">
                    {selectedNotifications.length} selected
                  </span>
                  <button className="mr-3 text-sm text-blue-600 hover:text-blue-800 flex items-center" onClick={() => markAsRead(selectedNotifications)} disabled={isMarkingRead} data-id="element-2545">
                    <CheckIcon size={16} className="mr-1" data-id="element-2546" />
                    Mark as read
                    {isMarkingRead && <span className="ml-1 animate-pulse" data-id="element-2547">...</span>}
                  </button>
                  {canManageNotifications && <button className="text-sm text-red-600 hover:text-red-800 flex items-center" onClick={() => confirmDelete(selectedNotifications)} disabled={isDeleting} data-id="element-2548">
                      <TrashIcon size={16} className="mr-1" data-id="element-2549" />
                      Delete
                      {isDeleting && <span className="ml-1 animate-pulse" data-id="element-2550">...</span>}
                    </button>}
                </div>
                <button className="text-sm text-gray-600 hover:text-gray-800" onClick={() => {
              setSelectedNotifications([]);
              setSelectAll(false);
            }} data-id="element-2551">
                  Cancel
                </button>
              </div>}
          </div>
          {/* Notifications List */}
          <div className="bg-white rounded-lg shadow mb-6 overflow-hidden" data-id="element-2552">
            {filteredNotifications.length === 0 ? <div className="p-8 text-center" data-id="element-2553">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-2554">
                  <BellOffIcon size={24} className="text-gray-400" data-id="element-2555" />
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-2556">
                  No notifications found
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-2557">
                  {searchQuery ? 'Try adjusting your search or filter criteria.' : activeFilter === 'unread' ? 'You have no unread notifications.' : activeFilter === 'read' ? 'You have no read notifications.' : 'You have no notifications at this time.'}
                </p>
                <div className="mt-6" data-id="element-2558">
                  <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" onClick={refreshNotifications} data-id="element-2559">
                    <RefreshCwIcon size={16} className="mr-2" data-id="element-2560" />
                    Refresh
                  </button>
                </div>
              </div> : <div className="overflow-x-auto" data-id="element-2561">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-2562">
                  <thead className="bg-gray-50" data-id="element-2563">
                    <tr data-id="element-2564">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2565">
                        <div className="flex items-center" data-id="element-2566">
                          <input type="checkbox" className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" checked={selectAll} onChange={handleSelectAll} data-id="element-2567" />
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2568">
                        Notification
                      </th>
                      {isSuperAdmin() && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2569">
                          MDA
                        </th>}
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2570">
                        Category
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2571">
                        Time
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2572">
                        Status
                      </th>
                      <th scope="col" className="relative px-6 py-3" data-id="element-2573">
                        <span className="sr-only" data-id="element-2574">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2575">
                    {filteredNotifications.map(notification => <tr key={notification.id} className={`hover:bg-gray-50 ${!notification.isRead ? 'bg-blue-50' : ''}`} data-id="element-2576">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2577">
                          <div className="flex items-center" data-id="element-2578">
                            <input type="checkbox" className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" checked={selectedNotifications.includes(notification.id)} onChange={() => handleSelectNotification(notification.id)} data-id="element-2579" />
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-2580">
                          <div className="flex items-start" data-id="element-2581">
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center" data-id="element-2582">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="ml-4" data-id="element-2583">
                              <div className="text-sm font-medium text-gray-900" data-id="element-2584">
                                {notification.title}
                              </div>
                              <div className="text-sm text-gray-500 line-clamp-2" data-id="element-2585">
                                {notification.message}
                              </div>
                            </div>
                          </div>
                        </td>
                        {isSuperAdmin() && <td className="px-6 py-4 whitespace-nowrap" data-id="element-2586">
                            <div className="text-sm text-gray-900 flex items-center" data-id="element-2587">
                              <BuildingIcon size={16} className="mr-2 text-gray-400" data-id="element-2588" />
                              {notification.mda}
                            </div>
                          </td>}
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2589">
                          {getNotificationCategoryBadge(notification.category)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2590">
                          <div className="text-sm text-gray-500 flex items-center" data-id="element-2591">
                            <CalendarIcon size={14} className="mr-1" data-id="element-2592" />
                            {formatRelativeTime(notification.date)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2593">
                          {notification.isRead ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-2594">
                              Read
                            </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-2595">
                              Unread
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2596">
                          <button onClick={() => viewNotificationDetail(notification.id)} className="text-green-600 hover:text-green-900 mr-3" data-id="element-2597">
                            <EyeIcon size={18} data-id="element-2598" />
                          </button>
                          {canManageNotifications && <button onClick={() => confirmDelete([notification.id])} className="text-red-600 hover:text-red-900" data-id="element-2599">
                              <TrashIcon size={18} data-id="element-2600" />
                            </button>}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
          {/* Notification Settings Card */}
          {canManageNotifications && <div className="bg-white rounded-lg shadow p-4 mb-6" data-id="element-2601">
              <div className="flex justify-between items-center mb-4" data-id="element-2602">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2603">
                  Notification Settings
                </h3>
                <button className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-2604">
                  <SettingsIcon size={16} className="mr-1" data-id="element-2605" />
                  Manage Settings
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2606">
                <div className="border border-gray-200 rounded-md p-4" data-id="element-2607">
                  <div className="flex items-center justify-between mb-4" data-id="element-2608">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-2609">
                      Email Notifications
                    </h4>
                    <div className="relative inline-block w-10 mr-2 align-middle select-none" data-id="element-2610">
                      <input type="checkbox" name="toggle" id="email-toggle" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" defaultChecked data-id="element-2611" />
                      <label htmlFor="email-toggle" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" data-id="element-2612"></label>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-2613">
                    Receive email notifications for important updates.
                  </p>
                </div>
                <div className="border border-gray-200 rounded-md p-4" data-id="element-2614">
                  <div className="flex items-center justify-between mb-4" data-id="element-2615">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-2616">
                      SMS Notifications
                    </h4>
                    <div className="relative inline-block w-10 mr-2 align-middle select-none" data-id="element-2617">
                      <input type="checkbox" name="toggle" id="sms-toggle" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" data-id="element-2618" />
                      <label htmlFor="sms-toggle" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" data-id="element-2619"></label>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-2620">
                    Receive SMS notifications for critical alerts.
                  </p>
                </div>
                <div className="border border-gray-200 rounded-md p-4" data-id="element-2621">
                  <div className="flex items-center justify-between mb-4" data-id="element-2622">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-2623">
                      Auto-Archive
                    </h4>
                    <div className="relative inline-block w-10 mr-2 align-middle select-none" data-id="element-2624">
                      <input type="checkbox" name="toggle" id="archive-toggle" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" defaultChecked data-id="element-2625" />
                      <label htmlFor="archive-toggle" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" data-id="element-2626"></label>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-2627">
                    Automatically archive read notifications after 30 days.
                  </p>
                </div>
              </div>
            </div>}
        </main>
      </div>
      {/* Notification Detail Modal */}
      {showNotificationDetail && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-2628">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-id="element-2629">
            {notifications.filter(n => n.id === showNotificationDetail).map(notification => <div key={notification.id} data-id="element-2630">
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2631">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-2632">
                      {getNotificationIcon(notification.type)}
                      <span className="ml-2" data-id="element-2633">{notification.title}</span>
                    </h3>
                    <button onClick={() => setShowNotificationDetail(null)} className="text-gray-400 hover:text-gray-500" data-id="element-2634">
                      <XCircleIcon size={20} data-id="element-2635" />
                    </button>
                  </div>
                  <div className="p-6" data-id="element-2636">
                    <div className="mb-4" data-id="element-2637">
                      <div className="flex items-center justify-between mb-2" data-id="element-2638">
                        <div className="flex items-center" data-id="element-2639">
                          {getNotificationCategoryBadge(notification.category)}
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${getNotificationBadgeClass(notification.type)}`} data-id="element-2640">
                            {notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center" data-id="element-2641">
                          <CalendarIcon size={14} className="mr-1" data-id="element-2642" />
                          {new Date(notification.date).toLocaleString()}
                        </div>
                      </div>
                      {isSuperAdmin() && <div className="text-sm text-gray-600 mb-2 flex items-center" data-id="element-2643">
                          <BuildingIcon size={16} className="mr-2 text-gray-400" data-id="element-2644" />
                          {notification.mda}
                        </div>}
                      <p className="text-gray-700" data-id="element-2645">{notification.message}</p>
                    </div>
                    {/* Sample action buttons based on notification type */}
                    {notification.category === 'application' && <div className="mt-6 flex items-center justify-between" data-id="element-2646">
                        <button className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700" data-id="element-2647">
                          View Application
                        </button>
                        {notification.type === 'warning' && <div className="flex space-x-3" data-id="element-2648">
                            <button className="px-4 py-2 bg-green-100 text-green-700 rounded-md text-sm hover:bg-green-200" data-id="element-2649">
                              Approve
                            </button>
                            <button className="px-4 py-2 bg-red-100 text-red-700 rounded-md text-sm hover:bg-red-200" data-id="element-2650">
                              Reject
                            </button>
                          </div>}
                      </div>}
                    {notification.category === 'payment' && <div className="mt-6" data-id="element-2651">
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700" data-id="element-2652">
                          View Payment Details
                        </button>
                      </div>}
                    {notification.category === 'service' && <div className="mt-6" data-id="element-2653">
                        <button className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700" data-id="element-2654">
                          View Service
                        </button>
                      </div>}
                  </div>
                  <div className="px-6 py-3 border-t border-gray-200 flex justify-between" data-id="element-2655">
                    <button onClick={() => setShowNotificationDetail(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-2656">
                      Close
                    </button>
                    {canManageNotifications && <button onClick={() => {
              confirmDelete([notification.id]);
              setShowNotificationDetail(null);
            }} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-2657">
                        Delete
                      </button>}
                  </div>
                </div>)}
          </div>
        </div>}
      {/* Delete Confirmation Modal */}
      {showConfirmDelete && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-2658">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-2659">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-2660">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2661">
                Confirm Delete
              </h3>
            </div>
            <div className="p-6" data-id="element-2662">
              <div className="flex items-center mb-4" data-id="element-2663">
                <div className="flex-shrink-0 bg-red-100 rounded-full p-2" data-id="element-2664">
                  <AlertCircleIcon size={24} className="text-red-600" data-id="element-2665" />
                </div>
                <div className="ml-3" data-id="element-2666">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-2667">
                    {notificationsToDelete.length === 1 ? 'Are you sure you want to delete this notification?' : `Are you sure you want to delete ${notificationsToDelete.length} notifications?`}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-2668">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-2669">
              <button onClick={() => setShowConfirmDelete(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-2670">
                Cancel
              </button>
              <button onClick={() => deleteNotifications(notificationsToDelete)} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-2671">
                Delete
              </button>
            </div>
          </div>
        </div>}
      {/* Add some global styles for toggle switches */}
      <style jsx data-id="element-2672">{`
        .toggle-checkbox:checked {
          right: 0;
          border-color: #10b981;
        }
        .toggle-checkbox:checked + .toggle-label {
          background-color: #10b981;
        }
        .toggle-checkbox {
          right: 0;
          z-index: 10;
        }
        .toggle-label {
          display: block;
          overflow: hidden;
          cursor: pointer;
          border-radius: 9999px;
        }
      `}</style>
    </div>;
};
export default Notifications;